import { useState, useEffect, useRef } from 'react';
import { forwardEmail } from '../api';

function EmailList({ emails, showSnippet, onEmailClick }) {
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, email: null });
  const [forwarding, setForwarding] = useState(null);
  const contextMenuRef = useRef(null);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu({ visible: false, x: 0, y: 0, email: null });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle right-click context menu
  const handleContextMenu = (e, email) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      email
    });
  };

  // Handle forward from context menu
  const handleForward = async (email) => {
    setContextMenu({ visible: false, x: 0, y: 0, email: null });
    setForwarding(email.id);
    try {
      await forwardEmail(email.id);
    } catch (err) {
      console.error('Failed to forward email:', err);
      alert('Failed to generate forward email. Please try again.');
    } finally {
      setForwarding(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow divide-y divide-gray-100 relative">
      {emails.map((email) => (
        <div
          key={email.id}
          onContextMenu={(e) => handleContextMenu(e, email)}
          className="relative"
        >
          <button
            onClick={() => onEmailClick(email)}
            className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {/* Subject */}
                <h3 className="text-sm font-medium text-gray-900 truncate">
                  {email.subject || '(No Subject)'}
                </h3>

                {/* Sender */}
                {(email.sender_name || email.sender_email) && (
                  <p className="text-sm text-gray-600 mt-1">
                    {email.sender_name && <span>{email.sender_name} </span>}
                    {email.sender_email && (
                      <span className="text-gray-400">&lt;{email.sender_email}&gt;</span>
                    )}
                  </p>
                )}

                {/* Snippet */}
                {showSnippet && email.snippet && (
                  <p
                    className="text-sm text-gray-500 mt-2 line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: email.snippet }}
                  />
                )}
              </div>

              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {/* Date */}
                <span className="text-xs text-gray-400">
                  {email.date ? formatDate(email.date) : ''}
                </span>

                {/* Indicators */}
                <div className="flex items-center gap-2">
                  {forwarding === email.id && (
                    <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {email.has_attachments === true && (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" title="Has attachments">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  )}
                  {email.folder && (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      {email.folder}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </button>
        </div>
      ))}

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            onClick={() => handleForward(contextMenu.email)}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
          >
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            Forward Email
          </button>
          <button
            onClick={() => {
              onEmailClick(contextMenu.email);
              setContextMenu({ visible: false, x: 0, y: 0, email: null });
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
          >
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Details
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default EmailList;
